import { useLayoutEffect } from 'react';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

const initialState = {
  menuOpen: false,
};

type MenuState = {
  menuOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  clear: () => void;
};

type UseMenuOptions = {
  /** When false, clears persisted state on mount (useful for drawer/mobile mode) */
  persistOpen?: boolean;
};

const useMenuStore = create<MenuState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        closeMenu: () =>
          set((state) => ({
            ...state,
            menuOpen: false,
          })),
        openMenu: () =>
          set((state) => ({
            ...state,
            menuOpen: true,
          })),
        clear: () => set(initialState),
      }),
      {
        name: 'sidebar-menu-state',
        partialize: (state) => ({ menuOpen: state.menuOpen }),
      },
    ),
  ),
);

/**
 * Hook to manage sidebar menu open/close state.
 * @param options.persistOpen - When false, clears persisted state and closes the menu on mount.
 *                              Useful for drawer mode where menu should start closed on page load.
 */
export const useMenu = (options?: UseMenuOptions) => {
  const state = useMenuStore();

  // When persistOpen is false, close menu and clear storage (before paint)
  useLayoutEffect(() => {
    if (options?.persistOpen === false) {
      const currentState = useMenuStore.getState();
      if (currentState.menuOpen) {
        currentState.closeMenu();
      }
      // Clear storage so nothing persists in drawer mode
      useMenuStore.persist.clearStorage();
    }
    // Only run on mount
  }, []);

  return state;
};

// Re-export store for direct access in tests
useMenu.getState = useMenuStore.getState;
useMenu.persist = useMenuStore.persist;
